import { Component, ElementRef, EventEmitter, Input, NgZone, Output, ViewChild, ChangeDetectionStrategy, inject } from "@angular/core";
import { LiveAnnouncer } from "@angular/cdk/a11y";

import {
  BaseComponent,
  CommunitiesService,
  CommunityFeatures,
  computeParticipationGate,
  EventBusService,
  File,
  FSP_COMMUNITY_STRING_ID,
  ParticipationGate,
  ProductRequirement,
  ProductService,
  UserService,
  WorkspaceService,
  environment,
  Notification,
  WindowService,
  HelperService,
  StorageService,
  StorageKeys,
  NotificationService,
  withShopAttribution,
  LanguageService,
  resolveLocalizedString,
  featuredUpdate,
  rememberFeaturedChange,
} from "@mypxplat/xplat/core";
import { TranslateService } from "@ngx-translate/core";
import { AppService, AvoService, BetaService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { ConfirmBetaAnnouncementEmail } from "../../modals/confirm-beta-announcement-email/confirm-beta-announcement-email.component";
import { DatePickerComponent } from "../../modals/date-picker/date-picker.component";
import { WorkspaceUploadComponent } from "../../modals/workspace-upload/workspace-upload.component";

import { take, takeUntil } from "rxjs/operators";
import { MessageInputComponent } from "../../message-input/message-input.component";
import { HtmlOverlayComponent } from "../../modals/html-overlay/html-overlay.component";
import { SearchContentComponent } from "../../modals/search-content/search-content.component";
import { RegisterProductComponent } from "../../modals/register-product/register-product.component";
import { SimpleSignupComponent } from "../../modals/simple-signup/simple-signup.component";
import { CommunitySettingsComponent } from "../../modals/community-settings/community-settings.component";
import { FileUploader } from "ng2-file-upload";
import { Router } from "@angular/router";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-create-post-input",
  templateUrl: "create-post-input.component.html",
  standalone: false,
})
export class CreatePostInputComponent extends BaseComponent {
  @ViewChild("msgInput") msgInput: MessageInputComponent;
  @ViewChild("subjectInput") subjectInput: MessageInputComponent;
  @ViewChild("confirmPublicPostModal") confirmPublicPostModal: ElementRef;
  private _selectedChannel: any;
  @Input() set selectedChannel(channel) {
    this.selectedChannels = [];
    if (channel && channel.string_id != "all_posts" && channel.string_id != "pinned_posts") {
      this.addChannel(channel);
      this._selectedChannel = channel;
    }
    this.applyChannelPostingRestriction();
  }
  get selectedChannel() {
    return this._selectedChannel;
  }

  @Input() topic: any;
  @Input() betaStringId: string;
  /**
   * Community this compose form writes new posts to. Defaults to Fender Studio Pro. Every
   * non-beta post authored here is tagged with this id via `community_string_id` so the
   * feed/search filters set up in DENG-727 task #33 can scope by community. Per-community
   * `features` (subject_required, editor, mentions_enabled, ...) are loaded from
   * CommunitiesService and stored on `communityFeatures` below.
   */
  @Input() communityStringId: string = FSP_COMMUNITY_STRING_ID;

  /**
   * Communities the member may post to from here, when the composer isn't tied to one (the
   * Communities hub on All communities). With more than one, which community is the first
   * thing the open form asks for, and nothing is picked for them; with exactly one it is
   * simply used. Null (every community page) keeps the fixed `communityStringId`.
   */
  @Input() set communityChoices(choices: Array<{ stringId: string; title: string }> | null) {
    this._communityChoices = choices?.length ? choices : null;
    const list = this._communityChoices;
    if (!list) {
      this.awaitingCommunity = false;
      this._syncFieldDescriptions();
      return;
    }
    if (list.length === 1) this.chooseCommunity(list[0].stringId);
    else if (!list.some((c) => c.stringId === this.communityStringId)) this.chooseCommunity("");
  }
  get communityChoices(): Array<{ stringId: string; title: string }> | null {
    return this._communityChoices;
  }
  private _communityChoices: Array<{ stringId: string; title: string }> | null = null;
  /** There's a choice to make and it hasn't been made: posting is held until it is. */
  public awaitingCommunity = false;
  /** The member picked (or changed) the community from `communityChoices`. */
  @Output() communityChosen: EventEmitter<string> = new EventEmitter();

  /**
   * Cached community feature flags for `communityStringId`. Populated in ngOnInit from
   * CommunitiesService. `undefined` before the cache warms — callers should tolerate that
   * (fall back to today's beta/FSP behavior when the config isn't loaded yet).
   */
  public communityFeatures: CommunityFeatures | undefined;

  /**
   * The admin "Allow @-mentions in the post editor" switch (`mentions_enabled`).
   * It has been on the community model and editable in admin all along, but no
   * composer ever read it, so turning it off did nothing. Fails open, matching
   * the other feature flags here, so an unseeded community still allows them.
   */
  get mentionsEnabled(): boolean {
    return this.communityFeatures?.mentions_enabled !== false;
  }

  /**
   * Whether the composer shows a subject field.
   *
   * A plain property rather than the expression in the template: bindings re-evaluate on
   * every change-detection pass, and this one has to agree exactly with the check in
   * `createPost()`. It previously did not — the field was rendered for beta programs only
   * while the validation ALSO demanded a subject from any community with
   * `features.subject_required`, so Tone Master Pro refused every post for a subject it
   * never offered anywhere to type.
   */
  public showSubject = false;

  /**
   * Participation gate — derived from `computeParticipationGate` in community.model.ts.
   * Pre-computed (not a getter) so template bindings don't rerun on every CD cycle;
   * `updatePostGate()` refreshes it when user / subDetails / community-cache changes.
   * The template reads `postGate.ok` for the primary yes/no and `postGate.reason` to
   * pick which upgrade prompt to render.
   */
  public postGate: ParticipationGate = { ok: false, reason: null };

  /**
   * Cached copy of the current community's `requiredProducts` for the "you need one of
   * these" prompt. Not a getter (per the project's "no method calls in templates" rule)
   * — refreshed in `updatePostGate` when the community cache changes.
   */
  public requiredProductsForDisplay: any[] = [];

  @Output() queuedPostsChanged: EventEmitter<any> = new EventEmitter();
  /**
   * A new post was written, with its id. The community feed doesn't need this (its Firestore
   * watcher picks the post up); a feed without one, like the Communities hub, adds it here.
   */
  @Output() postCreated: EventEmitter<any> = new EventEmitter();
  public createPostActive: boolean = false;
  public postSubject: string;
  public postContent: string;
  public sphereOnlyPost: boolean = false;
  public employeeOnlyPost: boolean = false;
  /** The toolbar's visibility menu, named for what's set — see `syncAudience`. */
  public audienceLabel = "Everyone";
  public pinToBetaProgram: boolean = false;
  /** Moderators and admins of this community can send a post straight to its Featured rail (DENG-1294). */
  public canFeature = false;
  public featurePost = false;
  public allowCommentsOnPin: boolean = false;
  public sendBetaProgramAnnouncementEmail: boolean = false;
  public selectedChannels: Array<any>;
  public shouldShowChannelSelector: boolean;

  /**
   * The member's signature for THIS community (DENG-847), or "" when unset. Refreshed from
   * the cached Firebase profile — `firebaseProfile$` fires when the settings modal saves,
   * so editing the signature updates an open composer without a reload.
   *
   * Beta programs are excluded: signatures are per COMMUNITY, and a beta post carries a
   * `beta_string_id` instead. `signatureForPost` is what the template and `sendPost` read.
   */
  public signature = "";
  /**
   * Per-post opt-out. Resets to true after every send and on cancel, so dropping the
   * signature once doesn't quietly disable it for everything you write afterwards.
   */
  public signatureEnabled = true;

  public uploadingFiles: any = false;
  // Never assigned, so the attach toolbar is never hidden while an upload is in flight.
  public uploadingFile: boolean;
  public uploadedAttachments: Array<File>;
  public queuedPosts: Array<any> = [];
  public publicProfileWarning: boolean = false;
  public uploader: FileUploader;
  public hasBaseDropZoneOver: any;
  public isBetaManager: boolean = false;
  public betaManagerNotificationPreferences: any;
  public scheduledPost = false;
  public scheduledPostDate: Date;
  public features = environment.features;

  /**
   * The field the last refused Send was about, and why (DENG-1274). The alert says it; this
   * also ties it to the field — `aria-invalid` on the picker, `aria-describedby` on each —
   * through the visually hidden `#create-post-error`. Cleared once the field changes.
   */
  public fieldError: "community" | "subject" | "body" | null = null;
  public fieldErrorText = "";
  public communityDescribedBy: string | null = null;
  public subjectDescribedBy: string | null = null;
  public bodyDescribedBy: string | null = null;
  /** "Posted", spoken: the composer folds away, so nothing on screen says it went. */
  private _announcer = inject(LiveAnnouncer);
  private _host = inject(ElementRef, { optional: true });

  constructor(
    userService: UserService,
    public communityService: WebCommunityService,
    public appService: AppService,
    public workspaceService: WorkspaceService,
    public eventBusService: EventBusService,
    private _zone: NgZone,
    public avoService: AvoService,
    private _win: WindowService,
    public betaService: BetaService,
    public router: Router,
    public helperService: HelperService,
    public storageService: StorageService,
    private notificationService: NotificationService,
    private communitiesService: CommunitiesService,
    private productService: ProductService,
    private languageService: LanguageService,
    private translate: TranslateService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.isBetaManager = this.user?.groups?.length ? this.user.groups.some((group: any) => group.name === "beta_manager") : false;
    this.uploader = new FileUploader({
      isHTML5: true,
      method: "PUT",
      queueLimit: 1,
      autoUpload: false,
      disableMultipart: true,
      url: "",
    });
    this.selectedChannels = this.selectedChannel && this.selectedChannel.string_id != "all_posts" ? [this.selectedChannel] : [];
    if (this.communityService.attachingExistingFile) {
      this.createPostActive = true;
      this.uploadedAttachments = [this.communityService.attachingExistingFile];
      this.communityService.attachingExistingFile = undefined;
    }
    if (this.communityService.attachingPublicFileLink) {
      this.createPostActive = true;
      this._win.setTimeout(() => {
        this.msgInput.setMessageValue(this.communityService.attachingPublicFileLink);
        this.msgInput.detectContent();
        this.communityService.attachingPublicFileLink = undefined;
      }, 300);
    }

    if (this.communityService.migratingPostMessage) {
      this.createPostActive = true;
      this._win.setTimeout(() => {
        this.postContent = this.communityService.migratingPostMessage;
        this.msgInput.setMessageValue(this.communityService.migratingPostMessage);
        this.msgInput.detectContent();
        this.communityService.migratingPostMessage = undefined;
      }, 300);
    }

    if (this.betaStringId) {
      // Beta programs always take a subject, whatever the community config says.
      this.showSubject = true;
      this.betaService.getBetaNotificationManagerPreferences(this.betaStringId).subscribe((result: any) => {
        this.betaManagerNotificationPreferences = result;
      });
    } else {
      // Subscribe (don't call load()). The parent <myp-community> is the single owner of
      // load()-firing; here we just react to whatever it eventually caches. Works whether
      // the cache is already warm (BehaviorSubject replays the last value) or lands later.
      // Fail-open: if the community isn't seeded yet, `communityFeatures` stays undefined
      // and the compose form falls back to today's FSP behavior.
      this.communitiesService.communities$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.communityFeatures = this.communitiesService.getCommunity(this.communityStringId)?.features;
        this.showSubject = !!this.communityFeatures?.subject_required;
        this._syncPostingIn();
        this.updatePostGate();
      });

      // Recompute the gate whenever the user record or their subscription details change.
      // `user.active_subscription` covers FSP+; `subDetails.play_subscription_normalized`
      // covers Fender Play — either can flip after an in-session upgrade/downgrade.
      this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updatePostGate());
      this.userService.subDetails$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updatePostGate());
      // Product ownership lives in ProductService (not on the user record). Re-run the
      // gate when the product list lands so a product-gated community resolves correctly
      // on first mount — until then we hold the gate closed with `reason: null` so no
      // wrong prompt flashes.
      this.productService.products$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updatePostGate());
      // profile.public affects the gate ("make your profile public first" path), so watch
      // it too. It's not a hot observable — refetch after `makePublic()` triggers CD.
      this.updatePostGate();

      // Fender Play subscription state lives on subDetails, which is loaded on-demand.
      // Trigger a fetch if it hasn't been populated yet so the gate resolves correctly
      // on first mount rather than after some other page happens to load it.
      if (!this.userService.subDetails && this.userService.user) {
        this.userService.getSubscriptionDetails().pipe(takeUntil(this.destroy$)).subscribe();
      }

      // The picker's channels: this community's, unless one is still to be chosen.
      if (!this.awaitingCommunity && this._channelOptionsFor !== this.communityStringId) this._loadChannelOptions(this.communityStringId);

      // Signature (DENG-847). `firebaseProfile$` re-emits when the settings modal saves,
      // so editing the signature there updates a composer that's already open.
      this.signature = this.communityService.getCommunitySignature(this.communityStringId);
      this.communityService.firebaseProfile$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.signature = this.communityService.getCommunitySignature(this.communityStringId);
      });
    }
  }

  /**
   * Refresh the gate from the current cached inputs. Called from ngOnInit + every
   * user/subDetails/community-cache change so the compose UI reacts to in-session
   * upgrades and profile flips without needing a reload.
   */
  /**
   * Channels whose `posting_groups` restriction this user can't start posts in. Replies are
   * unaffected — this only shapes the compose form. Non-beta only: beta programs use tags,
   * not community channels. See `CommunitiesService.canPostInChannel`.
   */
  private canPostIn(channel: any): boolean {
    if (this.betaStringId || !channel) return true;
    const full = this.communityService.taggableChannelsMap?.[channel.string_id] || channel;
    return this.communitiesService.canPostInChannel(this.userService.user, this.communityStringId, full);
  }

  /** The channel list in the "Add channel" picker, minus the ones this user can't post in. */
  public postableChannels: Array<any> = [];
  /**
   * This composer's own community's channels (`_loadChannelOptions`), for the picker. The
   * service's `taggableChannels` hold whichever community's channels were read LAST — in the
   * hub, which reads every community's, that was often another one (Fender Studio Pro's).
   * Null until loaded; beta programs keep the service's tag list.
   */
  private _channelOptions: Array<any> | null = null;
  /**
   * The community the post goes to, named on the "Post in" line when there's no choice to
   * make (one joined community, or a community's own page), with its accent for the dot.
   */
  public postingInTitle = "";
  public postingInAccent = "";
  private _channelOptionsFor = "";
  /**
   * The feed is filtered to a channel this user can't start posts in: the form is replaced by
   * a note saying so, rather than silently filing their post somewhere else.
   */
  public channelPostingLocked = false;
  public lockedChannelTitle: any = "";

  private applyChannelPostingRestriction() {
    const channel = this._selectedChannel;
    this.channelPostingLocked = !!channel && !this.canPostIn(channel);
    this.lockedChannelTitle = this.channelPostingLocked ? channel.title : "";
    if (this.channelPostingLocked) this.selectedChannels = (this.selectedChannels || []).filter((c) => c.string_id !== channel.string_id);
  }

  toggleChannelSelector() {
    this.shouldShowChannelSelector = !this.shouldShowChannelSelector;
    if (!this.shouldShowChannelSelector) return;
    // Into the list, so a keyboard user lands on the first channel rather than behind it.
    this._focusSoon(() => this._query("#channel-list .channel-button"));
    // Recomputed on open: the channel list and the user's groups can both land after init.
    if (this.betaStringId || !this._channelOptions) {
      this.postableChannels = (this.communityService.taggableChannels || []).filter((c) => this.canPostIn(c));
      return;
    }
    // Its own channels, judged as themselves — the service's map is keyed by channel id alone,
    // and ids repeat across communities.
    this.postableChannels = this._channelOptions.filter((c) => this.communitiesService.canPostInChannel(this.userService.user, this.communityStringId, c));
  }

  /** See `postingInTitle`. */
  private _syncPostingIn() {
    const community: any = this.communityStringId ? this.communitiesService.getCommunity(this.communityStringId) : null;
    this.postingInTitle = community?.title || "";
    this.postingInAccent = community?.branding?.accent_color || "";
  }

  /** See `_channelOptions`. The service caches per community, so this is usually instant. */
  private _loadChannelOptions(stringId: string) {
    this._channelOptionsFor = stringId;
    this._channelOptions = null;
    if (!stringId || this.betaStringId) return;
    this.communityService
      .getCommunityChannels(stringId)
      .pipe(take(1))
      .subscribe({
        next: (channels) => {
          if (this._channelOptionsFor !== stringId) return;
          const extras = (this.communityService.extraChannels || []).filter((item: any) => item.taggable);
          this._channelOptions = [...(channels || []), ...extras];
          if (this.shouldShowChannelSelector) {
            this.shouldShowChannelSelector = false;
            this.toggleChannelSelector();
          }
        },
        error: () => {},
      });
  }

  /**
   * Point the composer at a community picked from `communityChoices`: its features, subject
   * rule, signature, posting gate and channel picker all follow. Channels already added
   * belong to the previous community, so they're cleared.
   */
  chooseCommunity(stringId: string) {
    this.communityStringId = stringId || "";
    this.awaitingCommunity = !!this._communityChoices && this._communityChoices.length > 1 && !stringId;
    if (stringId) {
      this.communityFeatures = this.communitiesService.getCommunity(stringId)?.features;
      this.showSubject = !!this.betaStringId || !!this.communityFeatures?.subject_required;
      this.signature = this.communityService.getCommunitySignature(stringId);
      this.communityChosen.next(stringId);
    }
    // Its channels for the picker; none to offer until a community is picked.
    this._loadChannelOptions(stringId);
    this._syncPostingIn();
    if (!stringId) this.shouldShowChannelSelector = false;
    if (!this._selectedChannel || this._selectedChannel.community_string_id !== stringId) this.selectedChannels = [];
    if (stringId && this.fieldError === "community") this.fieldError = null;
    this._syncFieldDescriptions();
    this.updatePostGate();
  }

  private updatePostGate() {
    this._syncCanFeature();
    this.applyChannelPostingRestriction();
    const user = this.userService.user;
    // No community picked yet: nothing to gate against. Signed out still says so.
    if (this.awaitingCommunity) {
      this.postGate = user ? { ok: false, reason: null } : { ok: false, reason: "signed_out" };
      return;
    }
    const community = this.communitiesService.getCommunity(this.communityStringId);
    this.requiredProductsForDisplay = community?.requiredProducts || [];

    // Products live in ProductService, not on the user record. If this community gates
    // by product ownership and the product list isn't cached yet, hold the gate closed
    // silently (reason: null → no wrong prompt renders) and kick off the fetch. The
    // subscription in ngOnInit re-runs this method once `products$` emits.
    //
    // Only hold when the user is actually signed in — a signed-out user never has a
    // product list, and `products$` will never emit for them, so we'd stay in the
    // silent-loading state forever and never show the "sign in" prompt.
    if (user && community?.participation_condition === "product_owner" && !this.productService.hasGottenProducts) {
      this.postGate = { ok: false, reason: null };
      if (!this.productService.isGettingProducts) this.productService.getProducts();
      return;
    }

    this.postGate = computeParticipationGate({
      community,
      user,
      profilePublic: !!this.communityService.profile?.public,
      subDetails: this.userService.subDetails,
      // `user.groups` is typed `any` in the User model — comes back from paeapi as
      // Array<{name: string}> historically, occasionally Array<string>. Normalize
      // here so the pure gate helper stays shape-agnostic.
      userGroups: Array.isArray(user?.groups) ? user.groups.map((g: any) => (typeof g === "string" ? g : g?.name)).filter(Boolean) : undefined,
      ownsProduct: (req: ProductRequirement) => this.productService.ownsProductRequirement(req),
    });
  }

  upgrade() {
    this.router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }

  /**
   * Open the register-product modal. Same call shape as `<myp-dashboard-home>` — the
   * modal doesn't need a splashId in this context, we just want the user to add a
   * product to their account so the product-ownership gate re-evaluates on their next
   * post attempt. `products$` (which we subscribe to in ngOnInit) fires when the newly
   * registered product lands in `ProductService`, and `updatePostGate` re-runs.
   */
  registerProduct() {
    this.appService.showModal(RegisterProductComponent, {
      size: "lg",
      ariaLabelledBy: "modal-title",
      container: this.appService.appContainer,
    });
  }

  /** Open fender.com in a new tab — plain link, no region-shop routing. */
  shopNow() {
    window.open(withShopAttribution(environment.fenderDotComUrl), "_blank");
  }

  promptSignIn() {
    this.appService.promptSimpleSignup(SimpleSignupComponent).then(
      () => {},
      () => {}
    );
  }

  droppedFile(args) {
    this.attachFile(args);
    this.uploader.clearQueue();
  }

  makePublic() {
    this.communityService.updateProfile({ public: true }).then((result) => {
      this.updatePostGate();
      this.postInputFocus();
    });
  }

  postSubjectContentChanged(args) {
    this.postSubject = args;
    this.updatePostDrafts();
    if (this.fieldError === "subject") this._setFieldError(null);
  }

  postContentChanged(args) {
    this.postContent = args;
    this.updatePostDrafts();
    if (this.fieldError === "body") this._setFieldError(null);
  }

  updatePostDrafts() {
    if (this.betaStringId) {
      let existingDrafts = this.storageService.getItem(StorageKeys.BETA_COMMUNITY_POST_DRAFT_MAP);
      if (!existingDrafts) existingDrafts = {};
      let draftArgs = {
        subject: this.postSubject,
        body: this.postContent,
      };

      existingDrafts[this.betaStringId] = draftArgs;
      this.storageService.setItem(StorageKeys.BETA_COMMUNITY_POST_DRAFT_MAP, existingDrafts);
    } else {
      let draftArgs = { body: this.postContent };
      this.storageService.setItem(StorageKeys.COMMUNITY_POST_DRAFT, draftArgs);
    }
  }

  postInputFocus(type = "post") {
    if (!this.user) {
      this.promptSignIn();
      return;
    }
    if (this.communityService.profile?.public || this.betaStringId) {
      if (!this.createPostActive) {
        this.createPostActive = true;
        if (this.betaStringId) {
          let draftPostMap = this.storageService.getItem(StorageKeys.BETA_COMMUNITY_POST_DRAFT_MAP);
          if (draftPostMap && draftPostMap[this.betaStringId]) {
            this.postContent = draftPostMap[this.betaStringId].body;
            this.postSubject = draftPostMap[this.betaStringId].subject;
          }
        } else {
          let draftPost = this.storageService.getItem(StorageKeys.COMMUNITY_POST_DRAFT);
          if (draftPost) {
            this.postContent = draftPost.body;
          }
        }
        this._win.setTimeout(() => {
          if (type == "post" && this.betaStringId && !this.isBetaManager) this.populateBetaPostTemplate();
          if (this.betaStringId && this.subjectInput) {
            if (this.postSubject) this.subjectInput.setMessageValue(this.postSubject);
            this.subjectInput.focusMessageInput();
          } else if (this.msgInput) {
            if (this.postContent) this.msgInput.setMessageValue(this.postContent);
            this.msgInput.focusMessageInput();
          }
        }, 100);
      }
    } else {
      this.publicProfileWarning = true;
    }
  }

  public betaPostTemplatePopulated: boolean = false;
  populateBetaPostTemplate() {
    if (!this.betaPostTemplatePopulated) {
      this.betaPostTemplatePopulated = true;
      this.msgInput.setMessageValue(`
<p><strong>Issue Description</strong></p>
<p>Describe the issue...</p>
<p></p>
<p><strong>Steps To Reproduce</strong></p>
<p>How to recreate it...</p>
<p></p>
<p><strong>Observed Behavior</strong></p>
<p>What goes wrong...</p>
<p></p>
<p><strong>Expected Behavior</strong></p>
<p>What should have happened...</p>
<p></p>
<p><strong>Operating System</strong></p>
<p>Your os...</p>
<p></p>
      `);
    }
  }

  detectOperatingSystem() {
    const osPatterns = [
      { name: "Windows 10", pattern: /Windows NT 10\.0/i },
      { name: "Windows 8.1", pattern: /Windows NT 6\.3/i },
      { name: "Windows 8", pattern: /Windows NT 6\.2/i },
      { name: "Windows 7", pattern: /Windows NT 6\.1/i },
      { name: "Windows Vista", pattern: /Windows NT 6\.0/i },
      { name: "Windows XP", pattern: /Windows NT 5\.1|Windows XP/i },
      { name: "Windows 2000", pattern: /Windows NT 5\.0/i },
      { name: "Mac OS X", pattern: /Mac OS X ([\d_]+)/i },
      { name: "Mac OS", pattern: /Mac_PowerPC/i },
      { name: "Linux", pattern: /Linux/i },
      { name: "Ubuntu", pattern: /Ubuntu/i },
      { name: "iPhone", pattern: /iPhone OS ([\d_]+)/i },
      { name: "iPad", pattern: /iPad; CPU OS ([\d_]+)/i },
      { name: "Android", pattern: /Android ([\d\.]+)/i },
      { name: "Chrome OS", pattern: /CrOS/i },
    ];

    let osInfo = "Unknown OS";

    for (const os of osPatterns) {
      const match = navigator.userAgent.match(os.pattern);
      if (match) {
        const version = match[1] ? match[1].replace(/_/g, ".") : "";
        osInfo = os.name + (version ? ` ${version}` : "");
        break;
      }
    }

    // Additional checks for specific hardware information
    if (/Macintosh/i.test(navigator.userAgent) && /Intel/i.test(navigator.userAgent)) {
      osInfo += " (Intel)";
    } else if (/Macintosh/i.test(navigator.userAgent) && /Apple/i.test(navigator.userAgent)) {
      osInfo += " (Apple Silicon)";
    }

    return osInfo;
  }

  postInputBlur() {
    // this.createPostActive = false;
  }

  removeAttachment(attachment?) {
    if (attachment) {
      this.uploadedAttachments = this.uploadedAttachments.filter((item) => item.id != attachment.id);
    } else {
      this.uploadedAttachments = undefined;
      // this.uploadingFiles = undefined;
    }
  }

  watchVideo() {
    this.appService.showVideo(environment.fspPlusLearnMoreVideoId);
  }

  fileOverBase(e) {
    this.createPostActive = true;
    this.hasBaseDropZoneOver = e;
  }

  attachFile(droppedFile = false, showPasteMessage?) {
    const modalRef = this.appService.showModal(WorkspaceUploadComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.isPostAttachment = true;
    modalRef.componentInstance.message = showPasteMessage;
    if (droppedFile) modalRef.componentInstance.droppedFile = droppedFile;
    modalRef.componentInstance.uploadProgressChanged.pipe().subscribe((result) => {
      this.uploadingFiles.forEach((item) => {
        if (item.id == result.file.id) item.progress = result.progress;
      });
    });
    modalRef.componentInstance.fileCreated.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (!this.uploadingFiles) this.uploadingFiles = [];
      this.uploadingFiles.push(result);
    });
    modalRef.componentInstance.fileUploadFailed.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.uploadingFiles = this.uploadingFiles.filter((item) => item.id != result.id);
    });
    modalRef.componentInstance.fileUploadFinished.subscribe((result) => {
      this._zone.run(() => {
        if (!this.uploadedAttachments) this.uploadedAttachments = [];
        result.url = this.communityService.buildFileUrl(result.user_id, result);
        this.uploadedAttachments.push(result);
        this.uploadingFiles = this.uploadingFiles.filter((item) => item.id != result.id);
        if (this.queuedPosts.length) {
          if (!this.uploadingFiles.length) {
            this.queuedPosts.forEach((item, index) => {
              this.sendPost(item);
            });
            this.queuedPosts = [];
          }
        }
        this.uploadingFiles = this.uploadingFiles.filter((item) => item.id != result.id);
      });
    });
  }

  /** `fromPicker`: picked from the list, which closes — focus goes back to Add channel. */
  addChannel(channel, fromPicker = false) {
    if (!this.selectedChannels.filter((item) => item.string_id == channel.string_id).length) {
      this.selectedChannels.push(channel);
    }
    this.shouldShowChannelSelector = false;
    if (fromPicker) this._focusSoon(() => this._query(".add-channel-button") || this._query("#post-input"));
  }

  /** Escape in the channel list: close it, back on the button that opened it. */
  closeChannelSelector() {
    this.shouldShowChannelSelector = false;
    this._focusSoon(() => this._query(".add-channel-button"));
  }

  cancelCreatePost(dontConfirm?) {
    const rawTextPost = this.helperService.stripHtml(this.postContent);
    const rawSubjectPost = this.helperService.stripHtml(this.postSubject);
    if (dontConfirm || (!rawTextPost && !rawSubjectPost)) {
      this._closeCreatePost();
      return;
    }
    // The app's own dialog, not the browser's. `confirm` resolves false on cancel, never rejects.
    this.appService.confirm("Close the post editor? Your changes won't be saved.", "Discard", "Keep editing").then((discard) => {
      if (discard) this._closeCreatePost();
    });
  }

  /** Empty the composer and fold it back to its trigger. */
  private _closeCreatePost() {
    if (this.subjectInput?.emptyInput) this.subjectInput.emptyInput();
    this.msgInput.emptyInput();
    this.msgInput.selectedArticlesInMessage = [];
    this.msgInput.selectedLearnContentInMessage = [];
    this.msgInput.publicFileLinksInMessage = [];
    this.msgInput.messageEmbeds = [];
    this.msgInput.elligibleMentions = [];
    this.postSubject = "";
    this.postContent = "";
    this.signatureEnabled = true;
    this.pinToBetaProgram = undefined;
    this.featurePost = false;
    this.allowCommentsOnPin = undefined;
    this.sendBetaProgramAnnouncementEmail = undefined;
    this.createPostActive = false;
    // Where to post is asked afresh each time when there's a choice.
    if (this._communityChoices && this._communityChoices.length > 1) this.chooseCommunity("");
    this.removeAttachment();
    this.msgInput.displayPlaceholder = true;
    if (this.subjectInput) this.subjectInput.displayPlaceholder = true;
    this.betaPostTemplatePopulated = false;
    this._setFieldError(null);
    this._focusTriggerAfterClose();
    if (this.betaStringId) {
      let betaDrafts = this.storageService.getItem(StorageKeys.BETA_COMMUNITY_POST_DRAFT_MAP);
      delete betaDrafts[this.betaStringId];
      this.storageService.setItem(StorageKeys.BETA_COMMUNITY_POST_DRAFT_MAP, betaDrafts);
    } else {
      this.storageService.removeItem(StorageKeys.COMMUNITY_POST_DRAFT);
    }
  }

  /**
   * Append the community signature to a post body (DENG-847).
   *
   * Appended as plain TEXT, not markup, so anything angle-bracketed the member typed stays
   * the literal characters they typed rather than becoming live HTML in everyone's feed.
   *
   * `renderMessageBody` renders one `<br />` per `\n`, so this separator leaves a blank line
   * between the post and the signature. `updateCommunitySignature` stores the signature
   * verbatim, so any leading newlines the member added widen that gap.
   *
   * Replies append the same way — see `CommentComponent.sendReply`.
   *
   * Returns the body untouched for beta posts, when no signature is set, or when the
   * member switched it off for this post.
   */
  private _withSignature(body: string): string {
    if (this.betaStringId || !this.signature || !this.signatureEnabled) return body;
    return `${body}\n\n${this.signature}`;
  }

  /** The Featured toggle follows the community being posted to, so it re-checks on every gate refresh. */
  private _syncCanFeature() {
    this.canFeature =
      !!this.env.features?.communityFeaturedRail &&
      !this.betaStringId &&
      !this.awaitingCommunity &&
      !!this.communityStringId &&
      !!this.userService.user &&
      this.communitiesService.isCommunityModerator(this.userService.user, this.communityStringId);
    if (!this.canFeature) this.featurePost = false;
  }

  toggleFeaturePost() {
    this.featurePost = !this.featurePost;
  }

  /** Name the visibility menu for its current setting, after either checkbox changes. */
  syncAudience() {
    const limits: string[] = [];
    if (this.sphereOnlyPost) limits.push(`${this.env.studioOnePlusBrandName} only`);
    if (this.employeeOnlyPost) limits.push("Employees only");
    this.audienceLabel = limits.length ? limits.join(", ") : "Everyone";
  }

  /** Include / exclude the signature on the post being written. Resets on send / cancel. */
  toggleSignature() {
    this.signatureEnabled = !this.signatureEnabled;
  }

  /**
   * Shortcut from the composer to the signature field — behind "Add a signature" when the
   * member has none, and behind "Edit" when they do. Opens the same per-community settings
   * modal as the header gear: there is one place a signature is edited, and this is a door
   * to it rather than a second editor.
   *
   * No explicit refresh afterwards: saving the modal pushes `firebaseProfile$`, which the
   * subscription in ngOnInit already listens to.
   */
  openSignatureSettings() {
    const modalRef = this.appService.showModal(CommunitySettingsComponent, { size: "md" });
    modalRef.componentInstance.communityStringId = this.communityStringId;
    modalRef.componentInstance.communityTitle = this.communitiesService.getCommunity(this.communityStringId)?.title;
  }

  removeChannel(channel) {
    this.selectedChannels = this.selectedChannels.filter((item) => item.string_id != channel.string_id);
  }

  moveToBeta() {
    this.communityService.migratingPostMessage = this.postContent;
  }

  /**
   * The write didn't land. The post was already showing (it goes into `pendingPosts` before the
   * write, so the feed can show it at once) and nothing used to catch the rejection — so it
   * looked sent and was gone on refresh. Take the pending copy back out, leave the composer and
   * its text as they are, and say so, so the author can send again rather than retype it.
   */
  private _postFailed(post: any, err: any) {
    console.warn("[create-post-input] addPost failed", err);
    this.communityService.pendingPosts = this.communityService.pendingPosts.filter((pending) => pending !== post);
    this.appService.alert("Your post didn't send. Check your connection and try again — your text is still here.");
  }

  sendPost(queuedPost?, confirmed?) {
    if (!this.user) {
      this.promptSignIn();
      return;
    }
    if (!queuedPost && this.awaitingCommunity) {
      this._refuse("community", this.translate.instant("com.choose_community_first"));
      return;
    }
    // DISABLED 2026-09-15 — "are you sure you want to post publicly?" for anyone enrolled in a
    // beta. Well intentioned (a beta tester posting NDA'd detail in the open community is a real
    // risk) but far too noisy in practice: it fired on EVERY public post, and it lists one
    // "post in X instead" button per enrolled program — nine of them for staff, who are enrolled
    // in most. The template (`#confirmPublicPostModal`) and `moveToBeta()` are left in place so
    // this is a one-line revert if we want it back, ideally narrowed to private/NDA programs
    // rather than every enrollment.
    //
    // if (!confirmed && !this.betaStringId && this.betaService.enrolledBetas?.length) {
    //   this.appService.showModal(this.confirmPublicPostModal, { size: "sm" });
    // } else {
    {
      this.shouldShowChannelSelector = false;
      if (queuedPost) {
        let collection = this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection;
        this.communityService.addPost(queuedPost, collection).then(
          (result) => {
            this.postCreated.next({ ...queuedPost, id: result.id });
            this._announcer?.announce(this.translate.instant("com.a11y_post_published"), "polite");
            if (queuedPost.featured) {
              rememberFeaturedChange(result.id, true);
              this.communityService.featuredChanged$.next({ communityStringId: queuedPost.community_string_id, post: { ...queuedPost, id: result.id, key: result.id }, featured: true });
            }
            // Same fan-out as the main create path — queued posts (submitted after
            // attached-file uploads finish) are still parent posts landing in a
            // community, so followers who opted into `new_posts` should hear about them.
            if (!this.betaStringId && this.communityStringId && this.userService.user) {
              const authorName = `${this.userService.user.firstName || ""} ${this.userService.user.lastName || ""}`.trim();
              const snippet = queuedPost.subject || (typeof queuedPost.message === "string" ? this.helperService.htmlToText(queuedPost.message).slice(0, 200) : "");
              this.communityService
                .fanOutNewPost(this.communityStringId, {
                  postId: result.id,
                  subject: queuedPost.subject,
                  snippet,
                  authorName,
                  authorPhoto: this.userService.user.photoURL,
                })
                .catch((err) => console.warn("[create-post-input] fanOutNewPost (queued) failed", err));
            }
            this.cancelCreatePost(true);
          },
          (err) => this._postFailed(queuedPost, err)
        );
      } else if (this.showSubject && !this.postSubject) {
        this._refuse("subject", "Please enter a subject for your post.");
      } else if (this.betaStringId && !this.selectedChannels.length) {
        this.appService.alert("You must choose at least one tag for this post.");
      } else if (this.selectedChannels.some((c) => !this.canPostIn(c))) {
        // Belt and braces — the picker never offers these, but a channel's restriction can
        // change while the form is open.
        const blocked = this.selectedChannels.find((c) => !this.canPostIn(c));
        const title = resolveLocalizedString(blocked?.title, this.languageService.selectedLanguage?.code || "en", blocked?.string_id);
        this.appService.alert(this.translate.instant("com.channel_posting_restricted_remove", { channel: title }));
      } else if (this.postContent && this.postContent.trim()) {
        let args: any = {
          author: {
            name: this.userService.user.firstName + " " + this.userService.user.lastName,
            photo: this.userService.user.photoURL,
            id: this.userService.user.id,
          },
          created: new Date(),
        };
        if (this.betaStringId) {
          args.beta_string_id = this.betaStringId;
          args.reviewed = false;
          if (this.scheduledPost) {
            args.scheduled_date = this.scheduledPostDate;
          }
        } else if (this.communityStringId) {
          // Tag every non-beta post with its community so the Algolia + Firestore filters
          // added in DENG-727 task #33 can scope the feed. Historical posts got this via
          // the one-off backfill; new posts get it here.
          args.community_string_id = this.communityStringId;
        }
        if (this.sphereOnlyPost) args.sphere_only = true;
        if (this.employeeOnlyPost) args.employee_only = true;
        // Straight into the Featured rail. `created` is a client Date too; the rail orders by this.
        if (this.featurePost && this.canFeature) Object.assign(args, featuredUpdate(true, this.userService.user.id, new Date()));
        if (this.pinToBetaProgram) {
          args.pinned = true;
          // Pinned posts are read-only unless the manager explicitly opts them into comments.
          args.allow_comments = !!this.allowCommentsOnPin;
        }
        if (this.selectedChannels.length) {
          args.channels = [];
          this.selectedChannels.forEach((item) => {
            if (item.string_id != "all_posts") args.channels.push(item.string_id);
          });
        }
        if (!args.channels?.length) delete args.channels;
        if (this.topic) {
          args.topics = [this.topic.string_id];
        }
        if (this.uploadedAttachments || this.uploadingFiles) args.files = this.uploadedAttachments || this.uploadingFiles;
        if (this.msgInput.selectedArticlesInMessage && this.msgInput.selectedArticlesInMessage.length) {
          let articleEntites = [];
          this.msgInput.selectedArticlesInMessage.forEach((item) => {
            articleEntites.push({
              article: item.article,
              selector: item.selector,
              editorSelector: item.editorSelector,
            });
          });
          args.articles = articleEntites;
          this.msgInput.selectedArticlesInMessage.forEach((item) => {
            this.postContent = this.postContent.replace(item.editorSelector, item.selector + " ");
          });
        }

        if (this.msgInput.selectedLearnContentInMessage && this.msgInput.selectedLearnContentInMessage.length) {
          let learnContentEntities = [];
          this.msgInput.selectedLearnContentInMessage.forEach((item) => {
            learnContentEntities.push({
              content: item.content,
              selector: item.selector,
              editorSelector: item.editorSelector,
            });
          });
          args.learn_content = learnContentEntities;
          this.msgInput.selectedLearnContentInMessage.forEach((item) => {
            this.postContent = this.postContent.replace(item.editorSelector, item.selector + " ");
          });
        }

        if (this.msgInput.selectedCategoriesSectionsInMessage && this.msgInput.selectedCategoriesSectionsInMessage.length) {
          let sectionCategoryEntities = [];
          this.msgInput.selectedCategoriesSectionsInMessage.forEach((item) => {
            sectionCategoryEntities.push({
              category_or_section: item.category_or_section,
              selector: item.selector,
              editorSelector: item.editorSelector,
            });
          });
          args.categories_or_sections = sectionCategoryEntities;
          this.msgInput.selectedCategoriesSectionsInMessage.forEach((item) => {
            this.postContent = this.postContent.replace(item.editorSelector, item.selector + " ");
          });
        }

        if (this.msgInput.publicFileLinksInMessage && this.msgInput.publicFileLinksInMessage.length) {
          let publicFileLinkEntities = [];
          this.msgInput.publicFileLinksInMessage.forEach((item) => {
            publicFileLinkEntities.push({
              publicFile: item.publicFile,
              selector: item.selector,
              editorSelector: item.editorSelector,
            });
          });
          args.public_file_links = publicFileLinkEntities;
          this.msgInput.publicFileLinksInMessage.forEach((item) => {
            this.postContent = this.postContent.replace(item.editorSelector, item.selector + " ");
          });
        }

        const { messageEmbeds } = this.msgInput;
        if (messageEmbeds.length > 0) {
          args.embeds = messageEmbeds.map(({ id, type, subtype }) => {
            const embed = { id, type, subtype, src: "" };
            if (type == "youtube") {
              embed.src = `https://www.youtube.com/embed/${id}`;
            } else if (type == "soundcloud") {
              embed.src = `https://w.soundcloud.com/player/?url=${id}`;
            } else if (type == "spotify") {
              embed.src = `https://open.spotify.com/embed/${subtype}/${id}`;
            }
            return embed;
          });
        }
        args.subject = this.postSubject;
        // Mentions resolve last: finalizeMessage measures its character offsets
        // against the string it returns, so every other selector rewrite above
        // has to have happened already.
        const finalized = this.msgInput.finalizeMessage(this.postContent);
        if (finalized.mentions.length) args.mentions = finalized.mentions;
        this.msgInput.selectedMentionsInMessage = [];
        // Signature (DENG-847) goes on last, after every mention/embed selector has been
        // rewritten above — appending earlier would put it in front of those rewrites and
        // risk the replace passes walking over it. Appending cannot shift the
        // mention offsets, which are all before it.
        args.message = this._withSignature(finalized.message);
        // Also on its own, so readers can set it apart from the body (`splitPostSignature`).
        if (args.message !== finalized.message) args.signature = this.signature;
        // queue the post if the upload hasnt finished.
        if (this.uploadingFiles && this.uploadingFiles.length) {
          this.queuedPosts.push(args);
          this.queuedPostsChanged.next(this.queuedPosts);
          this.appService.alert("Your post will be submitted when your files have finished uploading.");
        } else {
          let collection = this.betaStringId ? `beta_community_posts` : this.env.communityFirestoreCollection;
          // A featured post goes to the rail, not the feed, so it gets no pending row there.
          if (!args.featured) this.communityService.pendingPosts.push(args);
          this.communityService.addPost(args, collection).then(
            (result) => {
              args.id = result.id;
              this.postCreated.next(args);
              if (args.featured) {
                args.key = result.id;
                rememberFeaturedChange(result.id, true);
                this.communityService.featuredChanged$.next({ communityStringId: args.community_string_id, post: args, featured: true });
              }
              this._announcer?.announce(this.translate.instant("com.a11y_post_published"), "polite");
              // add the id to the pending posts in the array

              // DENG-733 Phase 3 — fan out `community_new_post` notifications to followers.
              // Non-beta only (beta has its own beta-manager notification path below).
              // Fire-and-forget: a failed ping shouldn't block the post-create flow or
              // surface to the user; the post is already in Firestore, the ping is a bonus.
              if (!this.betaStringId && this.communityStringId && this.userService.user) {
                const authorName = `${this.userService.user.firstName || ""} ${this.userService.user.lastName || ""}`.trim();
                const snippet = args.subject || (typeof args.message === "string" ? this.helperService.htmlToText(args.message).slice(0, 200) : "");
                this.communityService
                  .fanOutNewPost(this.communityStringId, {
                    postId: result.id,
                    subject: args.subject,
                    snippet,
                    authorName,
                    authorPhoto: this.userService.user.photoURL,
                  })
                  .catch((err) => console.warn("[create-post-input] fanOutNewPost failed", err));
              }

              if (this.sendBetaProgramAnnouncementEmail) {
                const modalRef = this.appService.showModal(ConfirmBetaAnnouncementEmail, { size: "lg" });
                modalRef.componentInstance.program = this.betaService.betaDetailByStringId[this.betaStringId];
                modalRef.componentInstance.subject = this.postSubject;
                modalRef.componentInstance.messageHtml = this.postContent;
                modalRef.componentInstance.from = {
                  name: this.userService.user.firstName + " " + this.userService.user.lastName,
                  photo: this.userService.user.photoURL,
                  id: this.userService.user.id,
                };
                modalRef.componentInstance.directLink = `https://my.fender.com//beta/${this.betaStringId}/channels/all_posts`;
                modalRef.componentInstance.fileRefs = args.files || [];
              }
              if (args.pinned) this.eventBusService.emit(this.eventBusService.types.betaAnnouncementUpdated, true);
              if (args.mentions && args.mentions.length) {
                let mentionIDs = [];
                args.mentions.forEach((item) => {
                  mentionIDs.push(item.id);
                });
                if (mentionIDs.length) {
                  let notification = this.notificationService.createCommunityPostNotification(
                    "community_post_mention",
                    this.userService.user,
                    {
                      id: args.id,
                      message: args.message,
                      author: args.author,
                      files: args.files || [],
                      community_string_id: args.community_string_id,
                      beta_string_id: args.beta_string_id,
                    },
                    undefined,
                    undefined,
                    undefined,
                    this.communityStringId,
                    this.betaStringId
                  );
                  this.notificationService
                    .sendNotification({
                      notification: notification,
                      user_ids: mentionIDs,
                      clients: ["rollup", "mobile", "email"],
                    })
                    .subscribe();
                }
              }

              if (this.betaStringId && this.betaManagerNotificationPreferences) {
                let notificationIds = [];
                // send out notifications to beta managers if applicable
                for (var i in this.betaManagerNotificationPreferences) {
                  if (this.betaManagerNotificationPreferences[i].new) notificationIds.push(i);
                }

                if (notificationIds.length) {
                  let betaProgramName = "";
                  if (this.betaStringId) {
                    const betaDetail = this.betaService.betaDetailByStringId[this.betaStringId];
                    betaProgramName = betaDetail?.title && betaDetail.title.trim() ? betaDetail.title : this.betaStringId;
                  }
                  let notification = this.notificationService.createNewBetaPost(
                    this.userService.user,
                    { id: args.id, message: args.message, author: args.author, subject: args.subject, files: args.files || [] },
                    this.betaStringId,
                    betaProgramName
                  );
                  this.notificationService
                    .sendNotification({
                      notification: notification,
                      user_ids: notificationIds,
                      clients: ["rollup", "mobile", "email"],
                    })
                    .subscribe();
                }
              }

              const page = this.avoService.getPageProperties({ pageName: "Community" });
              const channels = this.getChannelName(this.selectedChannels.map((item) => item.title));
              const channelName = Array.isArray(channels) ? channels.join(",") : channels;
              const extra = {
                name: "Community",
                channelName: channelName,
                eventType: "post",
                postId: parseInt(result.id, 10) || undefined,
                attachmentExtension: this.uploadedAttachments?.[0]?.extension ?? undefined,
                interactionType: "created",
              };
              const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
              this.avoService.trackEvent().communityViewed(payload);
              this.cancelCreatePost(true);
            },
            (err) => this._postFailed(args, err)
          );
        }
        this.scheduledPost = false;
        this.scheduledPostDate = null;
      } else {
        this._refuse("body", "The textual content of your post cannot be empty.");
      }
    }
  }

  /** Refuse a Send over one field: say why, mark the field, and return to it once the alert closes. */
  private _refuse(field: "community" | "subject" | "body", message: string) {
    this._setFieldError(field, message);
    const ref: any = this.appService.alert(message);
    // ngb hands focus back to Send when the alert closes; the field is where the fix is.
    ref?.hidden?.pipe(take(1)).subscribe(() => {
      if (field === "community") this._query("#create-post-community")?.focus();
      else if (field === "subject") this.subjectInput?.focusMessageInput();
      else this.msgInput?.focusMessageInput();
    });
  }

  private _setFieldError(field: "community" | "subject" | "body" | null, message = "") {
    this.fieldError = field;
    this.fieldErrorText = field ? message : "";
    this._syncFieldDescriptions();
  }

  /** The ids each field's `aria-describedby` reads — set here, not computed in the template. */
  private _syncFieldDescriptions() {
    const error = (field: string) => (this.fieldError === field ? "create-post-error" : null);
    this.communityDescribedBy = [this.awaitingCommunity ? "create-post-community-hint" : null, error("community")].filter(Boolean).join(" ") || null;
    this.subjectDescribedBy = error("subject");
    this.bodyDescribedBy = error("body");
  }

  private _query(selector: string): HTMLElement | null {
    return this._host?.nativeElement?.querySelector(selector) || null;
  }

  private _focusSoon(target: () => HTMLElement | null | undefined) {
    if (!this._host) return;
    setTimeout(() => target()?.focus());
  }

  /**
   * The composer folds back to its trigger (cancelled, or posted), taking the focused control
   * with it: put focus on the trigger. Tried again once a closing confirm has had time to hand
   * focus back (ngb returns it to the now-removed close button, i.e. the body).
   */
  private _focusTriggerAfterClose() {
    const host: HTMLElement | undefined = this._host?.nativeElement;
    if (!host) return;
    const doc = host.ownerDocument;
    const tryFocus = () => {
      const active = doc.activeElement;
      if (active && active !== doc.body && !host.contains(active)) return;
      host.querySelector<HTMLElement>(".create-post-trigger")?.focus();
    };
    setTimeout(tryFocus);
    setTimeout(tryFocus, 400);
  }

  showArticle(article) {
    let modalRef = this.appService.showModal(HtmlOverlayComponent, { size: "lg" });
    modalRef.componentInstance.title = article.article.name;
    modalRef.componentInstance.html = '<style type="text/css">img { max-width: 100%; }</style>' + article.article.body;
  }

  removeArticle(article, e) {
    this.msgInput.removeSelectedArticle(article);
    e.stopPropagation();
  }

  removePublicFile(article, e) {
    this.msgInput.removePublicFile(article);
    e.stopPropagation();
  }

  removeLearnContent(item, e) {
    this.msgInput.removeSelectedLearnContent(item);
    e.stopPropagation();
  }

  removeEmbed(embed, e) {
    this.msgInput.removeEmbed(embed);
    e.stopPropagation();
  }

  attachSomething() {
    let args: any = {
      title: "What would you like to include in this post?",
      actions: ["Attach File", "Attach Article", "Attach Video"],
    };
    this.eventBusService.emit(this.eventBusService.types.showActionChooser, args);
    this.eventBusService
      .observe(this.eventBusService.types.actionChosen)
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          if (result == "Attach File") {
            this.attachFile();
          } else if (result == "Attach Article") {
            this.showAttach("articles");
          } else if (result == "Attach Video") {
            this.showAttach("videos");
          }
        }
      });
  }

  showAttach(type) {
    // Scrollable: the results scroll inside the body between a fixed header and footer, the
    // layout that scrolls reliably by touch on phones (the whole-content scroller didn't).
    const modalRef = this.appService.showModal(SearchContentComponent, { size: "lg", scrollable: true });
    modalRef.componentInstance.type = type;
    modalRef.componentInstance.contentSelected.pipe(take(1)).subscribe((result) => {
      if (type == "articles") {
        this.msgInput.selectedArticlesInMessage.push({ selector: false, editorSelector: false, article: result });
      } else {
        this.msgInput.selectedLearnContentInMessage.push({ selector: false, editorSelector: false, content: result });
      }
    });
  }

  getChannelName(channels) {
    return channels;
  }

  onScheduleChange(event: Event): void {
    if (!this.scheduledPost) {
      this.showDatePicker();
    } else {
      this.scheduledPost = false;
      this.scheduledPostDate = null;
    }
  }

  showDatePicker() {
    let modalInstance = this.appService.showModal(DatePickerComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalInstance.componentInstance.past = false;
    modalInstance.componentInstance.includeTimePicker = true;
    modalInstance.componentInstance.earliestYear = new Date().getFullYear();
    modalInstance.componentInstance.dateSelected.subscribe((date) => {
      if (date < new Date()) {
        this.appService.alert("You cannot schedule a post in the past.");
        return;
      }
      this.scheduledPost = true;
      this.scheduledPostDate = date;
    });
    modalInstance.dismissed.subscribe(() => {
      if (!this.scheduledPostDate) {
        this.scheduledPost = false;
      }
    });
    modalInstance.closed.subscribe(() => {
      if (!this.scheduledPostDate) {
        this.scheduledPost = false;
      }
    });
  }
}
